import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminStatusBadge } from "./AdminComponents";
import { adminApi } from "@/lib/adminApi";

function safeText(value, fallback = "-") {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
    return String(value);
}

function statusClass(value) {
    if (value === true) return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
    if (value === false) return "border-zinc-500/30 bg-zinc-500/10 text-zinc-300";
    const normalized = String(value || "").toLowerCase();
    if (["ok", "enabled", "configured", "healthy", "ready", "succeeded", "live"].includes(normalized)) {
        return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
    }
    if (["disabled", "not configured", "none", "queued", "upcoming", "local"].includes(normalized)) {
        return "border-zinc-500/30 bg-zinc-500/10 text-zinc-300";
    }
    if (["degraded", "stale", "running", "standby"].includes(normalized)) {
        return "border-amber-500/30 bg-amber-500/10 text-amber-200";
    }
    if (["error", "failed", "unavailable", "cancelled"].includes(normalized)) {
        return "border-red-500/30 bg-red-500/10 text-red-200";
    }
    return "border-sky-500/30 bg-sky-500/10 text-sky-200";
}

function StatusChip({ value, label }) {
    return (
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass(value)}`}>
            {label ? `${label}: ` : ""}
            {safeText(value)}
        </span>
    );
}

function Panel({ kicker, title, description, action, children }) {
    return (
        <section className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{kicker}</p>
                    <h2 className="mt-2 font-display text-3xl tracking-tight text-white">{title}</h2>
                    {description ? <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-400">{description}</p> : null}
                </div>
                {action}
            </div>
            <div className="mt-5">{children}</div>
        </section>
    );
}

function MetricGrid({ items }) {
    return (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {items.map((item) => (
                <div key={item.label} className="rounded-xl border border-white/10 bg-black/20 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-zinc-500">{item.label}</p>
                    <div className="mt-2">
                        {item.status ? <StatusChip value={item.value} /> : <p className="text-sm text-zinc-200">{safeText(item.value)}</p>}
                    </div>
                    {item.detail ? <p className="mt-2 text-xs leading-5 text-zinc-500">{item.detail}</p> : null}
                </div>
            ))}
        </div>
    );
}

function DisabledHint({ children }) {
    if (!children) return null;
    return <p className="mt-2 text-xs leading-5 text-zinc-500">{children}</p>;
}

function ActionButton({ label, disabled, disabledReason, busy, onClick, variant = "outline" }) {
    return (
        <div>
            <Button type="button" variant={variant} disabled={disabled || busy} onClick={onClick}>
                {busy ? "Working..." : label}
            </Button>
            {disabled ? <DisabledHint>{disabledReason}</DisabledHint> : null}
        </div>
    );
}

function formatDate(value) {
    if (!value) return "-";
    try {
        return new Intl.DateTimeFormat(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        }).format(new Date(value));
    } catch {
        return value;
    }
}

function shortId(value) {
    const text = String(value || "");
    if (text.length <= 16) return text || "-";
    return `${text.slice(0, 10)}...${text.slice(-4)}`;
}

function sanitizeError(error) {
    const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
    return String(detail || "Request failed.").replace(/(sk-|OPENAI_API_KEY|R2_SECRET_ACCESS_KEY|R2_ACCESS_KEY_ID)[^\s,)"]*/gi, "[redacted]");
}

function ReadinessPanel({ readiness, loading, onRefresh }) {
    const live = readiness?.live || {};
    const replay = readiness?.replay || {};
    const r2 = readiness?.r2 || {};
    const database = readiness?.database || {};
    return (
        <Panel
            kicker="System readiness"
            title="Runtime health"
            description="Read-only production readiness snapshot. This panel reports configuration and cache health without calling providers or OpenAI."
            action={<Button type="button" variant="outline" onClick={onRefresh}>{loading ? "Refreshing..." : "Refresh"}</Button>}
        >
            <MetricGrid
                items={[
                    { label: "Overall", value: readiness?.status || "unknown", status: true },
                    { label: "Database", value: database.connected === false ? "degraded" : "ok", status: true, detail: database.database || database.status },
                    { label: "Replay storage", value: replay.storage_backend || r2.replay_storage_backend || "unknown", status: true, detail: r2.healthy ? "Storage read path healthy" : r2.last_error || "Reported by readiness endpoint" },
                    { label: "R2", value: r2.configured ? "configured" : "not configured", status: true, detail: r2.bucket ? `Bucket: ${r2.bucket}` : "No bucket value exposed" },
                    { label: "Live provider polling", value: live.provider_polling_enabled, status: true },
                    { label: "OpenAI live analysis", value: live.openai_enabled, status: true },
                    { label: "Simulation", value: live.simulation_enabled, status: true },
                    { label: "Replay generation endpoints", value: replay.generation_endpoints_enabled, status: true },
                ]}
            />
        </Panel>
    );
}

function LiveOperationsPanel({ liveStatus, raceHealth, onRefresh, onAction, busyAction }) {
    const config = liveStatus?.config || {};
    const state = liveStatus?.state || {};
    const poller = liveStatus?.provider_poller || raceHealth?.provider_poller || {};
    const scheduler = liveStatus?.analysis_scheduler || raceHealth?.analysis_scheduler || {};
    const simulation = liveStatus?.simulation || {};
    const canUseOpenAi = Boolean(config.openai_enabled && config.openai_configured);
    const canUseSimulation = Boolean(config.simulation_enabled && config.simulation_controls_enabled);
    const canTestProvider = Boolean(config.provider_diagnostics_enabled);
    const latestEventCount = raceHealth?.latest_state?.event_count ?? raceHealth?.event_count ?? "-";
    const latestAnalysisCount = raceHealth?.latest_state?.analysis_count ?? raceHealth?.analysis_count ?? "-";

    return (
        <Panel
            kicker="Live dashboard"
            title="Live operations"
            description="Controls for cached live dashboard state. OpenAI/provider actions only call protected backend endpoints and stay disabled when backend flags are off."
            action={<Button type="button" variant="outline" onClick={onRefresh}>Refresh Status</Button>}
        >
            <MetricGrid
                items={[
                    { label: "Live state", value: state.status || "unknown", status: true, detail: state.message },
                    { label: "Session", value: state.session_id || "-", detail: state.updated_at ? `Updated ${formatDate(state.updated_at)}` : "" },
                    { label: "Provider polling", value: config.provider_polling_enabled, status: true, detail: poller.last_error || poller.last_success_at || "No provider polling run reported" },
                    { label: "Provider diagnostics", value: config.provider_diagnostics_enabled, status: true },
                    { label: "Simulation controls", value: canUseSimulation, status: true, detail: simulation.running ? "Simulation currently running" : simulation.message },
                    { label: "Analysis scheduler", value: scheduler.enabled, status: true, detail: scheduler.last_error || scheduler.last_run_at },
                    { label: "Latest events", value: latestEventCount },
                    { label: "Latest analysis cards", value: latestAnalysisCount },
                ]}
            />
            <div className="mt-5 flex flex-wrap gap-3">
                <ActionButton
                    label="Refresh Rule-Based Analysis"
                    busy={busyAction === "refresh-rules"}
                    onClick={() => onAction("refresh-rules")}
                />
                <ActionButton
                    label="Refresh OpenAI Analysis"
                    disabled={!canUseOpenAi}
                    disabledReason="OpenAI live analysis must be enabled and configured server-side."
                    busy={busyAction === "refresh-openai"}
                    onClick={() => onAction("refresh-openai")}
                />
                <ActionButton
                    label="Test Provider"
                    disabled={!canTestProvider}
                    disabledReason="Live provider diagnostics are disabled server-side."
                    busy={busyAction === "test-provider"}
                    onClick={() => onAction("test-provider")}
                />
                <ActionButton
                    label="Start Simulation"
                    disabled={!canUseSimulation}
                    disabledReason="Simulation controls are disabled server-side."
                    busy={busyAction === "start-simulation"}
                    onClick={() => onAction("start-simulation")}
                />
                <ActionButton
                    label="Stop Simulation"
                    disabled={!canUseSimulation}
                    disabledReason="Simulation controls are disabled server-side."
                    busy={busyAction === "stop-simulation"}
                    onClick={() => onAction("stop-simulation")}
                />
            </div>
        </Panel>
    );
}

function ReplayJobsPanel({ jobs, selectedJob, onSelectJob, onRefresh, onCancel, canGenerate, canUseOpenAi, onGenerate, busyAction }) {
    const [form, setForm] = useState({
        season: new Date().getFullYear(),
        race_slug: "",
        session_type: "race",
        provider: "openf1",
        generate_analysis: true,
        use_openai: false,
        force: false,
    });

    const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

    const submit = (event) => {
        event.preventDefault();
        onGenerate({
            ...form,
            season: form.season ? Number(form.season) : null,
            use_openai: Boolean(form.use_openai && canUseOpenAi),
            generate_analysis: Boolean(form.generate_analysis),
            force: Boolean(form.force),
        });
    };

    return (
        <Panel
            kicker="Replay generation jobs"
            title="Worker queue"
            description="Replay generation is admin/worker-first. Public replay routes only read ready bundles and never trigger generation."
            action={<Button type="button" variant="outline" onClick={onRefresh}>Refresh Jobs</Button>}
        >
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)]">
                <div className="overflow-hidden rounded-xl border border-white/10">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-white/10 text-sm">
                            <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-[0.16em] text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">Job</th>
                                    <th className="px-4 py-3">Race</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Progress</th>
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                                {jobs.length ? jobs.map((job) => (
                                    <tr key={job.job_id} className="align-top">
                                        <td className="px-4 py-3 font-mono text-xs text-zinc-300">{shortId(job.job_id)}</td>
                                        <td className="px-4 py-3 text-zinc-300">
                                            <div>{job.season || job.payload?.season || "-"}</div>
                                            <div className="text-xs text-zinc-500">{job.race_slug || job.payload?.race_slug || job.session_id}</div>
                                            <div className="text-xs text-zinc-500">{job.session_type || job.payload?.session_type || "race"}</div>
                                        </td>
                                        <td className="px-4 py-3"><AdminStatusBadge value={job.status} /></td>
                                        <td className="px-4 py-3 text-zinc-400">
                                            <div>{job.progress?.step || "-"}</div>
                                            <div className="text-xs text-zinc-500">{job.progress?.percent ?? 0}%</div>
                                            {job.error ? <div className="mt-1 text-xs text-red-200">{job.error}</div> : null}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-500">{formatDate(job.created_at)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-2">
                                                <Button type="button" size="sm" variant="outline" onClick={() => onSelectJob(job.job_id)}>
                                                    Details
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={!["queued", "running"].includes(String(job.status))}
                                                    onClick={() => onCancel(job.job_id)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-zinc-500" colSpan={6}>
                                            No replay generation jobs found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="flex flex-col gap-4">
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                        <h3 className="font-display text-2xl tracking-tight">Queue Generation</h3>
                        {!canGenerate ? (
                            <p className="mt-2 text-sm leading-6 text-zinc-400">
                                Replay generation endpoints are disabled. Use the CLI/worker or enable the flag in a controlled environment.
                            </p>
                        ) : null}
                        <form className="mt-4 flex flex-col gap-3" onSubmit={submit}>
                            <label className="flex flex-col gap-2 text-sm">
                                <span className="text-zinc-300">Season</span>
                                <Input value={form.season} onChange={(event) => updateForm("season", event.target.value)} />
                            </label>
                            <label className="flex flex-col gap-2 text-sm">
                                <span className="text-zinc-300">Race Slug</span>
                                <Input value={form.race_slug} placeholder="monaco" onChange={(event) => updateForm("race_slug", event.target.value)} />
                            </label>
                            <label className="flex flex-col gap-2 text-sm">
                                <span className="text-zinc-300">Session Type</span>
                                <Input value={form.session_type} onChange={(event) => updateForm("session_type", event.target.value)} />
                            </label>
                            <label className="flex flex-col gap-2 text-sm">
                                <span className="text-zinc-300">Provider</span>
                                <Input value={form.provider} onChange={(event) => updateForm("provider", event.target.value)} />
                            </label>
                            <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-300">
                                <input type="checkbox" checked={form.generate_analysis} onChange={(event) => updateForm("generate_analysis", event.target.checked)} />
                                Generate analysis JSON
                            </label>
                            <label className={`flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm ${canUseOpenAi ? "text-zinc-300" : "text-zinc-500"}`}>
                                <input type="checkbox" disabled={!canUseOpenAi} checked={Boolean(form.use_openai && canUseOpenAi)} onChange={(event) => updateForm("use_openai", event.target.checked)} />
                                Use OpenAI replay analysis
                            </label>
                            {!canUseOpenAi ? <DisabledHint>OpenAI replay analysis is disabled or not configured server-side.</DisabledHint> : null}
                            <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-300">
                                <input type="checkbox" checked={form.force} onChange={(event) => updateForm("force", event.target.checked)} />
                                Force regeneration
                            </label>
                            <Button type="submit" disabled={!canGenerate || busyAction === "queue-replay"}>
                                {busyAction === "queue-replay" ? "Queueing..." : "Queue Replay Job"}
                            </Button>
                        </form>
                    </div>
                    <JobDetailPanel job={selectedJob} />
                </div>
            </div>
        </Panel>
    );
}

function JobDetailPanel({ job }) {
    if (!job) {
        return (
            <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-500">
                Select a replay job to inspect status, output files, and sanitized details.
            </div>
        );
    }
    const output = job.output || {};
    return (
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-2xl tracking-tight">Job Details</h3>
                <AdminStatusBadge value={job.status} />
            </div>
            <div className="mt-4 grid gap-3 text-sm text-zinc-300">
                <div><span className="text-zinc-500">Job ID:</span> <span className="font-mono text-xs">{job.job_id}</span></div>
                <div><span className="text-zinc-500">Requested by:</span> {safeText(job.requested_by)}</div>
                <div><span className="text-zinc-500">Progress:</span> {safeText(job.progress?.step)} · {job.progress?.percent ?? 0}%</div>
                <div><span className="text-zinc-500">Manifest:</span> {safeText(output.manifest_key || output.manifest_path)}</div>
                <div><span className="text-zinc-500">Storage:</span> {safeText(output.storage_backend)} · {safeText(output.replay_prefix || output.r2_prefix)}</div>
                <div><span className="text-zinc-500">Started:</span> {formatDate(job.started_at)}</div>
                <div><span className="text-zinc-500">Finished:</span> {formatDate(job.finished_at)}</div>
                {job.error ? <div className="text-red-200"><span className="text-zinc-500">Error:</span> {job.error}</div> : null}
            </div>
            <details className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <summary className="cursor-pointer text-sm text-zinc-300">View sanitized JSON</summary>
                <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap text-xs text-zinc-400">
                    {JSON.stringify(job, null, 2)}
                </pre>
            </details>
        </div>
    );
}

function StoragePanel({ readiness }) {
    const r2 = readiness?.r2 || {};
    const replay = readiness?.replay || {};
    return (
        <Panel
            kicker="Storage / R2"
            title="Replay bundle storage"
            description="Replay bundles are read through backend storage abstraction. Secrets and signed tokens are intentionally hidden."
        >
            <MetricGrid
                items={[
                    { label: "Backend", value: replay.storage_backend || r2.replay_storage_backend || "unknown", status: true },
                    { label: "R2 configured", value: r2.configured ? "configured" : "not configured", status: true },
                    { label: "Bucket", value: r2.bucket ? "configured" : "not configured", status: true },
                    { label: "Replay prefix", value: r2.replay_prefix || "replay-bundles" },
                    { label: "Local path", value: replay.local_dir || "not used" },
                    { label: "Can read storage", value: replay.can_read_storage, status: true, detail: r2.last_error },
                ]}
            />
        </Panel>
    );
}

function SafetyFlagsPanel({ readiness, liveStatus }) {
    const live = readiness?.live || {};
    const replay = readiness?.replay || {};
    const config = liveStatus?.config || {};
    const flags = [
        ["Provider polling", live.provider_polling_enabled],
        ["OpenAI live analysis", live.openai_enabled],
        ["Live analysis scheduler", live.analysis_scheduler?.enabled],
        ["Simulation", live.simulation_enabled],
        ["Simulation controls", config.simulation_controls_enabled],
        ["Provider diagnostics", config.provider_diagnostics_enabled],
        ["Replay generation endpoints", replay.generation_endpoints_enabled],
        ["Replay worker", replay.worker_enabled],
    ];
    return (
        <Panel
            kicker="Safety flags"
            title="Feature gates"
            description="Operations UI does not enable dangerous features by itself. Actions stay disabled until backend flags are deliberately enabled."
        >
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {flags.map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
                        <span className="text-sm text-zinc-300">{label}</span>
                        <StatusChip value={value ? "enabled" : "disabled"} />
                    </div>
                ))}
            </div>
        </Panel>
    );
}

export default function AdminOperationsPage() {
    const [readiness, setReadiness] = useState(null);
    const [liveStatus, setLiveStatus] = useState(null);
    const [raceHealth, setRaceHealth] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busyAction, setBusyAction] = useState("");

    const loadAll = async () => {
        setLoading(true);
        setError("");
        try {
            const [nextReadiness, nextLiveStatus, nextRaceHealth, nextJobs] = await Promise.all([
                adminApi.system.readiness(),
                adminApi.liveOps.status(),
                adminApi.liveOps.health().catch(() => null),
                adminApi.replayOps.jobs(),
            ]);
            setReadiness(nextReadiness);
            setLiveStatus(nextLiveStatus);
            setRaceHealth(nextRaceHealth);
            setJobs(nextJobs?.items || []);
        } catch (nextError) {
            setError(sanitizeError(nextError));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
        const timer = setInterval(loadAll, 30000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const replayCanGenerate = Boolean(readiness?.replay?.generation_endpoints_enabled);
    const replayCanUseOpenAi = Boolean(
        (readiness?.replay?.openai_enabled || readiness?.openai?.replay_enabled) &&
        readiness?.openai?.configured
    );

    const runLiveAction = async (action) => {
        const labels = {
            "refresh-rules": "Refresh rule-based analysis now?",
            "refresh-openai": "Refresh OpenAI live analysis now? This can spend API credits.",
            "test-provider": "Run one protected provider test snapshot now?",
            "start-simulation": "Start live simulation mode?",
            "stop-simulation": "Stop live simulation mode?",
        };
        if (!window.confirm(labels[action] || "Run this operation?")) return;
        setBusyAction(action);
        setNotice("");
        setError("");
        try {
            if (action === "refresh-rules") {
                await adminApi.liveOps.refreshAnalysis({ force: true, use_openai: false });
            } else if (action === "refresh-openai") {
                await adminApi.liveOps.refreshAnalysis({ force: true, use_openai: true });
            } else if (action === "test-provider") {
                await adminApi.liveOps.testProvider({ write: false });
            } else if (action === "start-simulation") {
                await adminApi.liveOps.startSimulation();
            } else if (action === "stop-simulation") {
                await adminApi.liveOps.stopSimulation();
            }
            setNotice("Operation completed.");
            await loadAll();
        } catch (nextError) {
            setError(sanitizeError(nextError));
        } finally {
            setBusyAction("");
        }
    };

    const loadJobDetails = async (jobId) => {
        setError("");
        try {
            const response = await adminApi.replayOps.job(jobId);
            setSelectedJob(response.job);
        } catch (nextError) {
            setError(sanitizeError(nextError));
        }
    };

    const cancelJob = async (jobId) => {
        if (!window.confirm(`Cancel replay generation job ${shortId(jobId)}?`)) return;
        setBusyAction("cancel-job");
        try {
            await adminApi.replayOps.cancelJob(jobId);
            setNotice("Replay job cancelled.");
            await loadAll();
        } catch (nextError) {
            setError(sanitizeError(nextError));
        } finally {
            setBusyAction("");
        }
    };

    const queueGeneration = async (payload) => {
        if (!window.confirm("Queue this replay generation job? This should only be used in controlled environments.")) return;
        setBusyAction("queue-replay");
        setError("");
        try {
            const response = await adminApi.replayOps.queueGeneration(payload);
            setNotice(response.message || "Replay generation job queued.");
            await loadAll();
            if (response.job_id) await loadJobDetails(response.job_id);
        } catch (nextError) {
            setError(sanitizeError(nextError));
        } finally {
            setBusyAction("");
        }
    };

    const headline = useMemo(() => {
        const status = readiness?.status || "unknown";
        const storage = readiness?.replay?.storage_backend || readiness?.r2?.replay_storage_backend || "unknown";
        return `Readiness ${status} · Replay storage ${storage}`;
    }, [readiness]);

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">OutLapped Operations</span>
                        <h1 className="mt-2 font-display text-4xl tracking-tight">Admin Operations</h1>
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-400">
                            Live dashboard, replay generation, cache, storage, and readiness controls. Status loads are safe read-only requests; generation, provider, simulation, and OpenAI actions require explicit clicks and backend flags.
                        </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 md:items-end">
                        <StatusChip value={readiness?.status || "unknown"} />
                        <p className="text-xs text-zinc-500">{headline}</p>
                    </div>
                </div>
            </div>
            {error ? <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">{error}</div> : null}
            {notice ? <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{notice}</div> : null}
            {loading && !readiness ? (
                <div className="rounded-2xl border border-white/10 bg-[#11151b] px-4 py-10 text-center text-zinc-400">
                    Loading operations status...
                </div>
            ) : (
                <>
                    <ReadinessPanel readiness={readiness} loading={loading} onRefresh={loadAll} />
                    <LiveOperationsPanel
                        liveStatus={liveStatus}
                        raceHealth={raceHealth}
                        onRefresh={loadAll}
                        onAction={runLiveAction}
                        busyAction={busyAction}
                    />
                    <ReplayJobsPanel
                        jobs={jobs}
                        selectedJob={selectedJob}
                        onSelectJob={loadJobDetails}
                        onRefresh={loadAll}
                        onCancel={cancelJob}
                        canGenerate={replayCanGenerate}
                        canUseOpenAi={replayCanUseOpenAi}
                        onGenerate={queueGeneration}
                        busyAction={busyAction}
                    />
                    <StoragePanel readiness={readiness} />
                    <SafetyFlagsPanel readiness={readiness} liveStatus={liveStatus} />
                </>
            )}
        </div>
    );
}
